import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Building2,
  Check,
  Globe,
  Mail,
  MapPin,
  Pencil,
  Phone,
  PhoneMissed,
  Trash2,
  User,
  UserCheck,
} from 'lucide-react';

import StatusPill from '@/components/StatusPill';
import MonthBars from '@/components/MonthBars';
import {
  HeadActionsCard,
  HeadJournal,
  TrialGraceCard,
  showTrialGrace,
  type HeadManage,
  type TrialGraceInfo,
} from '@/components/HeadActions';
import { CallMarkButtons, useCallMarker } from '@/components/CallMarks';
import {
  AssignManagerSheet,
  DeleteEstablishmentSheet,
  EditEstablishmentSheet,
  type DeleteUsage,
} from '@/components/EstablishmentSheets';
import { apiGet } from '@/lib/api';
import { readBackState, type BackState } from '@/lib/back-nav';
import { RESULT_LABEL, type CallView } from '@/lib/calls';
import { clearEstablishmentsCache, patchCachedSubscription } from '@/lib/establishments';
import { formatBps, formatDate, formatDateTime, formatKop } from '@/lib/format';
import { monthLong } from '@/lib/period';
import { telHref } from '@/lib/phone';
import {
  daysLeftLabel,
  networkPointLabel,
  networkPointTone,
  pluralDays,
  pluralEstablishments,
  statusTone,
  type NetworkPoint,
  type OwnerNetwork,
  type SubscriptionInfo,
} from '@/lib/subscription';
import { useIsHeadManager, useIsSalesDirector } from '@/store/auth.store';

interface Detail {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  logoUrl: string | null;
  domain: string | null;
  timezone: string;
  currency: string;
  createdAt: string;
  owner: { name: string | null; email: string | null; phone: string | null } | null;
  referral: {
    attachedAt: string | null;
    commissionBps: number | null;
    source: string | null;
    endsAt: string | null;
  };
  /** Точка привязана к тому, кто смотрит. */
  mine?: boolean;
  /** Чья точка — приходит только главному менеджеру. */
  referrer?: { id: string; name: string; code: string; email: string; phone: string | null } | null;
  subscription: SubscriptionInfo | null;
  /** Сеть владельца: все его точки с собственными статусами (справка). */
  network: OwnerNetwork;
  totals: {
    paymentsKopecks: number;
    paymentsCount: number;
    commissionKopecks: number;
    pendingKopecks: number;
  };
  /** Оплаты подписки точкой за вычетом возвратов. */
  turnover?: Turnover;
  /** Оборот самого заведения: выручка через кассу за 30 дней. */
  revenue30d?: { days: number; kopecks: number; orders: number; averageKopecks: number };
  /** Действия со сроком — только у главного менеджера. */
  manage?: HeadManage | null;
  /** Разовые дни пробного — только у обычного менеджера. */
  trialGrace?: TrialGraceInfo | null;
  /** Только у руководителя: можно ли удалить и что пропадёт. */
  director?: { canDelete: boolean; deleteBlockedReason: string | null; usage: DeleteUsage } | null;
  /** Прозвон: последние отметки. Нет у старого сервера. */
  calls?: CallView[];
  payments: Array<{
    id: string;
    amountKopecks: number;
    method: string | null;
    paidAt: string;
    acceptedBy?: { kind: 'manager' | 'operator'; name: string | null } | null;
    accrual: { kind: string; amountKopecks: number; bps: number; paidOut: boolean } | null;
  }>;
}

interface Turnover {
  totalKopecks: number;
  refundedKopecks: number;
  thisMonthKopecks: number;
  last12mKopecks: number;
  firstPaidAt: string | null;
  lastPaidAt: string | null;
  byMonth: Array<{ month: string; kopecks: number }>;
}

const SOURCE_LABELS: Record<string, string> = {
  link: 'по ссылке',
  promo: 'по промокоду',
  manual: 'вручную оператором',
  transfer: 'передано руководителем',
  director: 'назначил руководитель',
};

const PERIOD_LABELS: Record<string, string> = {
  MONTHLY: 'месяц',
  YEARLY: 'год',
};

/**
 * Способ оплаты. В базе лежит либо код (`cash`), либо человекочитаемое из
 * ЮKassa («Mir *6670»), либо служебная строка `manual …` — её менеджеру
 * показывать нечего.
 */
function methodLabel(method: string | null): string | null {
  if (!method) return null;
  if (method === 'cash') return 'наличные';
  if (method === 'transfer') return 'перевод';
  if (method.startsWith('manual')) return 'внесено оператором';
  return method;
}

function acceptedByLabel(p: Detail['payments'][number]): string | null {
  if (!p.acceptedBy) return null;
  if (p.acceptedBy.kind === 'operator') return 'принял оператор';
  return p.acceptedBy.name ? `принял ${p.acceptedBy.name}` : 'принял менеджер';
}

export default function EstablishmentDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isHead = useIsHeadManager();
  const isDirector = useIsSalesDirector();
  const back = readBackState(location.state);
  const [sheet, setSheet] = useState<'edit' | 'assign' | 'delete' | null>(null);
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // После действия главного карточку перечитываем тихо, не теряя прокрутку.
  const [version, setVersion] = useState(0);
  const loadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void apiGet<Detail>(`/establishments/${id}`, { silent: true })
      .then((d) => {
        if (cancelled) return;
        loadedIdRef.current = d.id;
        setData(d);
      })
      .catch((err: AxiosError) => {
        if (cancelled) return;
        const status = err.response?.status;
        // Точка стала недоступна (сняли роль, отвязали) — честная ошибка; сбой
        // сети при уже показанной карточке — тост, данные остаются.
        if (status === 403 || status === 404 || loadedIdRef.current !== id) {
          loadedIdRef.current = null;
          setData(null);
          setError('Заведение не найдено или больше не привязано к вам');
        } else {
          toast.error('Не удалось обновить карточку');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, version, isHead]);

  // «← Заведения» — назад по истории, если пришли из списка: тот же вид и прокрутка.
  const backHref = back?.backTo ?? '/establishments';
  const goBack = (e: React.MouseEvent) => {
    if (!back) return;
    e.preventDefault();
    navigate(-back.backSteps);
  };
  const nextBackState: BackState | undefined = back
    ? { ...back, backSteps: back.backSteps + 1 }
    : undefined;
  const backLabel = back?.backLabel ?? 'Заведения';

  const onActionDone = (sub: SubscriptionInfo | null) => {
    if (id) patchCachedSubscription(id, sub);
    setVersion((v) => v + 1);
  };

  // Правка, назначение, удаление меняют строки списка — кеш сбрасываем целиком.
  const onDirectorChange = (message: string) => {
    setSheet(null);
    clearEstablishmentsCache();
    toast.success(message);
    setVersion((v) => v + 1);
  };

  const onDeleted = (name: string) => {
    setSheet(null);
    clearEstablishmentsCache();
    toast.success(`«${name}» удалено`);
    if (back) navigate(-back.backSteps);
    else navigate('/establishments', { replace: true });
  };

  // Другой id — показываем загрузку; тот же (перечитали после действия) — старые данные до ответа.
  const showLoading = loading && (!data || data.id !== id);
  if (showLoading) return <div className="text-ink/50 text-sm">Загрузка…</div>;

  if (error || !data) {
    return (
      <div className="max-w-3xl space-y-3">
        <div className="card p-4 text-sm text-ink/70">{error ?? 'Заведение не найдено'}</div>
        <Link to={backHref} onClick={goBack} className="btn-ghost !px-0">
          <ArrowLeft size={16} /> К списку
        </Link>
      </div>
    );
  }

  const sub = data.subscription;
  const tone = sub ? statusTone(sub) : 'slate';
  const daysCls =
    tone === 'red' ? 'text-rose-600' : tone === 'amber' ? 'text-amber-600' : 'text-ink';
  // Главный открывает и чужие точки: комиссия по ним идёт их менеджеру, не ему.
  const showMyCommission = !isHead || data.mine !== false;

  return (
    <div className="max-w-3xl space-y-4">
      <Link
        to={backHref}
        onClick={goBack}
        className="inline-flex items-center gap-1.5 text-sm text-ink/55 hover:text-ink"
      >
        <ArrowLeft size={16} /> {backLabel}
      </Link>

      <div className="card p-4 sm:p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-paper">
            {data.logoUrl ? (
              <img src={data.logoUrl} alt={data.name} className="h-full w-full object-contain" />
            ) : (
              <Building2 size={22} className="text-ink/25" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold leading-tight">{data.name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <StatusPill sub={sub} />
              <span className={`text-sm font-medium ${daysCls}`}>{daysLeftLabel(sub)}</span>
            </div>
          </div>
        </div>
        <CallButtons
          ownerName={data.owner?.name ?? null}
          ownerPhone={data.owner?.phone ?? null}
          placePhone={data.phone}
        />
      </div>

      {data.calls ? (
        <CallsCard
          establishmentId={data.id}
          calls={data.calls}
          onChanged={() => setVersion((v) => v + 1)}
        />
      ) : null}

      {data.revenue30d ? <RevenueCard revenue={data.revenue30d} /> : null}

      {!isHead && showTrialGrace(data.trialGrace) ? (
        <TrialGraceCard
          establishmentId={data.id}
          subscription={sub}
          info={data.trialGrace}
          onDone={onActionDone}
        />
      ) : null}

      {isHead && data.manage ? (
        <HeadActionsCard
          establishmentId={data.id}
          subscription={sub}
          manage={data.manage}
          onDone={onActionDone}
        />
      ) : null}

      {showMyCommission ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="card p-4">
            <div className="text-xs text-ink/50">Оплатили подписки</div>
            <div className="mt-1 text-xl font-semibold">{formatKop(data.totals.paymentsKopecks)}</div>
            <div className="mt-0.5 text-[11px] text-ink/40">{data.totals.paymentsCount} платежей</div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-ink/50">Моя комиссия</div>
            <div className="mt-1 text-xl font-semibold">{formatKop(data.totals.commissionKopecks)}</div>
            <div className="mt-0.5 text-[11px] text-ink/40">
              ставка {data.referral.commissionBps != null ? formatBps(data.referral.commissionBps) : '—'}
            </div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-ink/50">Из них к выплате</div>
            <div className="mt-1 text-xl font-semibold">{formatKop(data.totals.pendingKopecks)}</div>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="card p-4">
            <div className="text-xs text-ink/50">Оплатили подписки</div>
            <div className="mt-1 text-xl font-semibold">{formatKop(data.totals.paymentsKopecks)}</div>
            <div className="mt-0.5 text-[11px] text-ink/40">{data.totals.paymentsCount} платежей</div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-ink/50">Менеджер</div>
            {data.referrer ? (
              <>
                <div className="mt-1 text-xl font-semibold truncate">{data.referrer.name}</div>
                <div className="mt-0.5 text-[11px] text-ink/40">
                  {data.referrer.code}
                  {data.referral.commissionBps != null ? ` · ставка ${formatBps(data.referral.commissionBps)}` : ''}
                </div>
              </>
            ) : (
              <div className="mt-1 text-xl font-semibold text-ink/40">не привязан</div>
            )}
          </div>
        </div>
      )}

      {data.turnover ? <TurnoverCard turnover={data.turnover} /> : null}

      <div className="card divide-y divide-line/60">
        <div className="flex items-center justify-between gap-3 px-4 py-2">
          <div className="py-1 text-sm font-medium">Контакты</div>
          {isDirector ? (
            <button type="button" className="btn-ghost !min-h-[36px] !px-2.5 !py-1.5" onClick={() => setSheet('edit')}>
              <Pencil size={15} /> Изменить
            </button>
          ) : null}
        </div>
        <InfoRow icon={<MapPin size={15} />} label="Адрес" value={data.address} />
        <InfoRow
          icon={<Phone size={15} />}
          label="Телефон заведения"
          value={data.phone}
          href={data.phone ? `tel:${data.phone.replace(/[^\d+]/g, '')}` : null}
        />
        <InfoRow
          icon={<User size={15} />}
          label="Владелец"
          value={data.owner?.name ?? null}
        />
        <InfoRow
          icon={<Mail size={15} />}
          label="Email владельца"
          value={data.owner?.email ?? null}
          href={data.owner?.email ? `mailto:${data.owner.email}` : null}
        />
        <InfoRow
          icon={<Phone size={15} />}
          label="Телефон владельца"
          value={data.owner?.phone ?? null}
          href={data.owner?.phone ? `tel:${data.owner.phone.replace(/[^\d+]/g, '')}` : null}
        />
        <InfoRow
          icon={<Globe size={15} />}
          label="Домен меню"
          value={data.domain}
          href={data.domain ? `https://${data.domain}` : null}
        />
      </div>

      <div className="card divide-y divide-line/60">
        <div className="px-4 py-3 text-sm font-medium">Подписка</div>
        {sub ? (
          <>
            <InfoRow label="Тариф" value={sub.plan} />
            <InfoRow
              label="Стоимость"
              value={`${formatKop(sub.amountKopecks)} / ${PERIOD_LABELS[sub.billingPeriod] ?? sub.billingPeriod}`}
            />
            <InfoRow label="Оплачено до" value={formatDate(sub.accessEndsAt)} />
            {sub.trialEndsAt ? <InfoRow label="Триал до" value={formatDate(sub.trialEndsAt)} /> : null}
          </>
        ) : (
          <div className="px-4 py-3 text-sm text-ink/50">
            {isHead
              ? 'Подписки ещё нет — её заведёт первое действие: триал или оплата.'
              : 'Подписки нет — комиссия начнёт капать с первой оплаты.'}
          </div>
        )}
      </div>

      {data.network.ownerEstablishmentCount > 1 ? (
        <div className="card divide-y divide-line/60">
          <div className="px-4 py-3">
            <div className="text-sm font-medium">
              Сеть владельца · {data.network.ownerEstablishmentCount}{' '}
              {pluralEstablishments(data.network.ownerEstablishmentCount)}
            </div>
            <p className="mt-0.5 text-xs text-ink/50">
              У каждой точки своя подписка, оплата и комиссия — здесь показано только «{data.name}».
            </p>
          </div>
          {data.network.establishments.map((p) => (
            <NetworkRow
              key={p.id}
              point={p}
              currentId={data.id}
              isHead={isHead}
              backState={nextBackState}
            />
          ))}
        </div>
      ) : null}

      <div className="card divide-y divide-line/60">
        <div className="flex items-center justify-between gap-3 px-4 py-2">
          <div className="py-1 text-sm font-medium">
            {showMyCommission ? 'Моя привязка' : 'Привязка менеджера'}
          </div>
          {isDirector ? (
            <button type="button" className="btn-ghost !min-h-[36px] !px-2.5 !py-1.5" onClick={() => setSheet('assign')}>
              <UserCheck size={15} /> {data.referrer || data.mine ? 'Сменить' : 'Назначить'}
            </button>
          ) : null}
        </div>
        {!showMyCommission && !data.referrer ? (
          <div className="px-4 py-3 text-sm text-ink/50">Заведение ни к кому не привязано.</div>
        ) : (
          <>
            {!showMyCommission && data.referrer ? (
              <InfoRow
                icon={<UserCheck size={15} />}
                label="Менеджер"
                value={`${data.referrer.name} · ${data.referrer.code}`}
                href={data.referrer.phone ? `tel:${data.referrer.phone.replace(/[^\d+]/g, '')}` : null}
              />
            ) : null}
            <InfoRow label="Привязано" value={formatDate(data.referral.attachedAt)} />
            <InfoRow
              label="Источник"
              value={data.referral.source ? SOURCE_LABELS[data.referral.source] ?? data.referral.source : null}
            />
            {data.referral.endsAt ? (
              <InfoRow label="Комиссия до" value={formatDate(data.referral.endsAt)} />
            ) : null}
          </>
        )}
        <InfoRow label="Заведение создано" value={formatDate(data.createdAt)} />
      </div>

      {isHead && data.manage ? <HeadJournal actions={data.manage.actions} /> : null}

      <div className="card overflow-hidden">
        <div className="border-b border-line px-4 py-3 text-sm font-medium">
          История оплат ({data.totals.paymentsCount})
        </div>
        {data.payments.length === 0 ? (
          <div className="px-4 py-6 text-sm text-ink/50">Оплат ещё не было.</div>
        ) : (
          <ul className="divide-y divide-line/60">
            {data.payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="font-medium">{formatKop(p.amountKopecks)}</div>
                  <div className="mt-0.5 text-xs text-ink/50">
                    {formatDate(p.paidAt)}
                    {methodLabel(p.method) ? ` · ${methodLabel(p.method)}` : ''}
                    {acceptedByLabel(p) ? ` · ${acceptedByLabel(p)}` : ''}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  {p.accrual ? (
                    <>
                      <div
                        className={`font-semibold ${
                          p.accrual.kind === 'STORNO' ? 'text-rose-600' : 'text-emerald-600'
                        }`}
                      >
                        {p.accrual.kind === 'STORNO' ? '' : '+'}
                        {formatKop(p.accrual.amountKopecks)}
                      </div>
                      <div className="text-[11px] text-ink/40">
                        {formatBps(p.accrual.bps)} · {p.accrual.paidOut ? 'выплачено' : 'к выплате'}
                      </div>
                    </>
                  ) : showMyCommission ? (
                    <div className="text-[11px] text-ink/40">без начисления</div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isDirector && data.director ? (
        <section className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="text-sm">
            <div className="font-medium">Удалить заведение</div>
            <p className="mt-0.5 text-ink/55">
              {data.director.canDelete
                ? 'Полностью и навсегда — для профилей с ложными данными, которые ни разу не платили.'
                : data.director.deleteBlockedReason}
            </p>
          </div>
          <button
            type="button"
            className="btn shrink-0 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-45"
            disabled={!data.director.canDelete}
            onClick={() => setSheet('delete')}
          >
            <Trash2 size={16} /> Удалить
          </button>
        </section>
      ) : null}

      {sheet === 'edit' ? (
        <EditEstablishmentSheet
          establishmentId={data.id}
          profile={{
            name: data.name,
            address: data.address,
            phone: data.phone,
            ownerName: data.owner?.name ?? null,
            ownerPhone: data.owner?.phone ?? null,
            hasOwner: !!data.owner,
            networkSize: data.network.ownerEstablishmentCount,
          }}
          onClose={() => setSheet(null)}
          onSaved={(changed) => onDirectorChange(changed.length ? 'Профиль заведения сохранён' : 'Изменений нет')}
        />
      ) : null}
      {sheet === 'assign' ? (
        <AssignManagerSheet
          establishmentId={data.id}
          current={data.referrer ? { id: data.referrer.id, name: data.referrer.name } : null}
          onClose={() => setSheet(null)}
          onDone={(r) => onDirectorChange(r ? `Менеджер — ${r.name}` : 'Привязка снята')}
        />
      ) : null}
      {sheet === 'delete' && data.director ? (
        <DeleteEstablishmentSheet
          establishmentId={data.id}
          name={data.name}
          usage={data.director.usage}
          onClose={() => setSheet(null)}
          onDeleted={() => onDeleted(data.name)}
        />
      ) : null}
    </div>
  );
}

/**
 * Прозвон: отметить итог звонка (с комментарием) и история отметок. Своя
 * отметка отменяется 15 минут — на случай «ошибся кнопкой».
 */
function CallsCard({
  establishmentId,
  calls,
  onChanged,
}: {
  establishmentId: string;
  calls: CallView[];
  onChanged: () => void;
}) {
  const [comment, setComment] = useState('');
  const [all, setAll] = useState(false);
  const { mark, undo, busyId } = useCallMarker(() => onChanged());
  const shown = all ? calls : calls.slice(0, 5);

  return (
    <section className="card space-y-3 p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h2 className="text-sm font-medium">Прозвон</h2>
        <span className="text-xs text-ink/45">
          {calls.length ? `отметок: ${calls.length}${calls.length >= 30 ? '+' : ''}` : 'ещё не звонили'}
        </span>
      </div>
      <input
        className="input"
        maxLength={500}
        placeholder="Комментарий к звонку — необязательно"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        aria-label="Комментарий к звонку"
      />
      <CallMarkButtons
        className="grid grid-cols-2"
        busy={busyId === establishmentId}
        onMark={(res) => {
          void mark(establishmentId, res, comment).then((ok) => {
            if (ok) setComment('');
          });
        }}
      />
      {shown.length ? (
        <ul className="divide-y divide-line/60 border-t border-line/60">
          {shown.map((c) => (
            <li key={c.id} className="flex items-start justify-between gap-3 py-2.5">
              <div className="flex min-w-0 items-start gap-2 text-sm">
                {c.result === 'ANSWERED' ? (
                  <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                ) : (
                  <PhoneMissed size={15} className="mt-0.5 shrink-0 text-amber-600" />
                )}
                <div className="min-w-0">
                  <div className="font-medium">{RESULT_LABEL[c.result]}</div>
                  <div className="text-xs text-ink/50">
                    {formatDateTime(c.createdAt)} · {c.manager.name}
                  </div>
                  {c.comment ? <div className="mt-0.5 break-words text-ink/70">{c.comment}</div> : null}
                </div>
              </div>
              {c.canUndo ? (
                <button
                  type="button"
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-orange hover:bg-orange/10"
                  onClick={() => void undo(c.id)}
                >
                  Отменить
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {calls.length > 5 ? (
        <button type="button" className="text-xs font-medium text-orange hover:underline" onClick={() => setAll((v) => !v)}>
          {all ? 'Свернуть' : `Показать все (${calls.length})`}
        </button>
      ) : null}
    </section>
  );
}

/** Звонок из шапки карточки: владельцу (решает про подписку) и в заведение. */
function CallButtons({
  ownerName,
  ownerPhone,
  placePhone,
}: {
  ownerName: string | null;
  ownerPhone: string | null;
  placePhone: string | null;
}) {
  const owner = telHref(ownerPhone);
  const place = telHref(placePhone);
  if (!owner && !place) return null;
  const name = ownerName?.trim();
  return (
    <div className="mt-4 flex flex-wrap gap-2 border-t border-line/60 pt-4">
      {owner ? (
        <a
          href={owner}
          className="btn max-w-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        >
          <Phone size={16} className="shrink-0" />
          <span className="truncate">{name ? `Владелец · ${name}` : 'Позвонить владельцу'}</span>
        </a>
      ) : null}
      {place && place !== owner ? (
        <a href={place} className="btn border border-line bg-white text-ink/75 hover:bg-paper">
          <Phone size={16} className="shrink-0" /> Заведение
        </a>
      ) : null}
    </div>
  );
}

/** Оборот заведения — касса за 30 дней: ради неё клиент и платит за подписку. */
function RevenueCard({ revenue: r }: { revenue: NonNullable<Detail['revenue30d']> }) {
  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h2 className="text-sm font-medium">
          Оборот за {r.days} {pluralDays(r.days)}
        </h2>
        <span className="text-xs text-ink/45">выручка через кассу</span>
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-3">
        <div>
          <dt className="text-xs text-ink/50">Выручка</dt>
          <dd className={`mt-0.5 text-lg font-semibold tabular-nums ${r.kopecks > 0 ? '' : 'text-ink/35'}`}>
            {formatKop(r.kopecks)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-ink/50">Заказов</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums">{r.orders}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink/50">Средний чек</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums">
            {r.orders > 0 ? formatKop(r.averageKopecks) : '—'}
          </dd>
        </div>
      </dl>
      {r.kopecks === 0 ? (
        <p className="mt-3 text-xs text-amber-700">
          Продаж через кассу не было — стоит позвонить и узнать, почему не работают.
        </p>
      ) : null}
    </section>
  );
}

function TurnoverCard({ turnover: t }: { turnover: Turnover }) {
  const [month, setMonth] = useState<string | null>(null);
  const selected = month ?? t.byMonth[t.byMonth.length - 1]?.month ?? null;
  const selectedRow = t.byMonth.find((m) => m.month === selected);
  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h2 className="text-sm font-medium">Оплаты подписки</h2>
        <span className="text-xs text-ink/45">за вычетом возвратов</span>
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-3">
        <div>
          <dt className="text-xs text-ink/50">Этот месяц</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums">{formatKop(t.thisMonthKopecks)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink/50">12 месяцев</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums">{formatKop(t.last12mKopecks)}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink/50">За всё время</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums">{formatKop(t.totalKopecks)}</dd>
        </div>
      </dl>
      {t.last12mKopecks > 0 ? (
        <>
          <div className="mt-4">
            <MonthBars
              size="sm"
              label="Оплаты подписки по месяцам"
              data={t.byMonth.map((m) => ({ month: m.month, value: m.kopecks }))}
              selected={selected}
              onSelect={setMonth}
            />
          </div>
          {selectedRow ? (
            <div className="mt-2 text-sm">
              <span className="text-ink/55">{monthLong(selectedRow.month)}:</span>{' '}
              <span className="font-medium tabular-nums">{formatKop(selectedRow.kopecks)}</span>
            </div>
          ) : null}
        </>
      ) : null}
      <p className="mt-3 text-xs text-ink/50">
        {t.firstPaidAt
          ? `Первая оплата ${formatDate(t.firstPaidAt)} · последняя ${formatDate(t.lastPaidAt)}`
          : 'Оплат подписки ещё не было'}
        {t.refundedKopecks > 0 ? ` · возвращено ${formatKop(t.refundedKopecks)}` : ''}
      </p>
    </section>
  );
}

const POINT_TONES = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-rose-50 text-rose-700 border-rose-200',
  slate: 'bg-ink/5 text-ink/60 border-line',
} as const;

/** Строка точки сети: своя плашка и свой остаток срока; закрытые точки — без ссылки. */
function NetworkRow({
  point,
  currentId,
  isHead,
  backState,
}: {
  point: NetworkPoint;
  currentId: string;
  isHead: boolean;
  backState: BackState | undefined;
}) {
  const isCurrent = point.id === currentId;
  const canOpen = point.canOpen ?? point.mine;
  const remaining =
    point.status === 'none'
      ? null
      : point.accessState === 'blocked'
        ? 'доступ закрыт'
        : point.accessState === 'grace'
          ? 'просрочено'
          : point.daysLeft == null
            ? null
            : `${point.daysLeft} ${pluralDays(point.daysLeft)}`;

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0 text-sm">
        {canOpen && !isCurrent ? (
          <Link
            to={`/establishments/${point.id}`}
            state={backState}
            className="font-medium text-orange hover:underline"
          >
            {point.name}
          </Link>
        ) : (
          <span className={isCurrent ? 'font-medium' : 'text-ink/70'}>{point.name}</span>
        )}
        {isCurrent ? <span className="ml-1.5 text-xs text-ink/40">эта карточка</span> : null}
        {!point.mine && !isHead ? <span className="ml-1.5 text-xs text-ink/40">не ваша привязка</span> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {remaining ? <span className="text-xs text-ink/50">{remaining}</span> : null}
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${POINT_TONES[networkPointTone(point)]}`}
        >
          {networkPointLabel(point)}
        </span>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value, href }: {
  icon?: React.ReactNode;
  label: string;
  value: string | null;
  href?: string | null;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-ink/55">
        {icon ? <span className="text-ink/35">{icon}</span> : null}
        {label}
      </div>
      <div className="min-w-0 text-right text-sm">
        {value ? (
          href ? (
            <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="break-words font-medium text-orange hover:underline">
              {value}
            </a>
          ) : (
            <span className="break-words font-medium">{value}</span>
          )
        ) : (
          <span className="text-ink/35">не указан</span>
        )}
      </div>
    </div>
  );
}
